import { storeToRefs } from 'pinia'
import { HostBrowserApis } from '../../utils/host-browser-apis'
import version from '../../../package.json'
import type { AugmentedProperties, NtpPageType } from './types'
import { useScreenSizeProperties } from './useScreenSizeProperties'
import { useLayoutSkeletonStore } from '../../store/feature-flags/useLayoutSkeletonStore'
import debug from '../../utils/console'
import {
  getInitialBatchContent,
  getLatestMoreNewsBatchContent,
  clearMoreNewsBatch,
  hasInitialBatch,
  hasMoreNewsBatch,
  getPageviewSeed,
} from './useFetchedContent'
import {
  getTestSectionContent,
  hasTestSectionContent,
} from './useTestSectionContent'
import { createUUID } from '../../utils/string/uuid'
import { isHomepageRoute } from '../../utils/url/isHomepageRoute'
import { TARGETING_KEY_COOKIE } from '../../openfeature/utils'

// Stable per-page-load ID used to correlate the OneNews NtpPage_Viewed Mixpanel
// event with the QZ page_view event via the pageview_id URL param.
let _pageviewId: string | null = null
let _userId: string | undefined = undefined

export function getPageviewId(): string {
  return (_pageviewId ??= createUUID())
}

export function getUserId(): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${TARGETING_KEY_COOKIE}=`))
  return (_userId ??= match
    ? decodeURIComponent(match.split('=')[1] ?? '')
    : undefined)
}

const logger = debug.withTag('analytics:helper')

declare global {
  interface Window {
    isUserIdle?: boolean
  }
}

function findTypeTagInSearchUrl(url: string | null) {
  if (url == null) {
    return null
  }

  const pattern =
    '([0-9]{1}_[0-9]{4}_[0-9]{3}_[0-9]{4}_[0-9]{3}(_[0-9]{3})?_[0-9]{6})'
  const regexMatching = url.match(pattern)

  if (regexMatching != null) {
    return regexMatching[0]
  }

  return null
}

export async function getTypeTag(): Promise<string> {
  const DEFAULT_TYPE_TAG = '0_1000_100_1000_100_691231'
  try {
    const installInfo = await HostBrowserApis.getConfig()
    if (installInfo?.type_tag) {
      return installInfo.type_tag
    }

    const searchUrl = installInfo?.search_url || null
    const typeTagFromSearchUrl = findTypeTagInSearchUrl(searchUrl)
    if (typeTagFromSearchUrl) {
      return typeTagFromSearchUrl
    }

    const getTypeTagFromUrl = () => {
      const url = new URL(location.href)
      return url.searchParams.get('type') || '0_1000_100_1000_100_691231'
    }

    const typeTagFromUrl = getTypeTagFromUrl()
    if (typeTagFromUrl) {
      return typeTagFromUrl
    }
    return DEFAULT_TYPE_TAG
  } catch (e) {
    logger.error(`Error getting type tag: ${e}`)
    return DEFAULT_TYPE_TAG
  }
}

function getNtpPath(): string {
  const route = useRoute()

  if (route.path.includes('/v8/article/')) {
    return '/v8/article'
  } else if (route.path.includes('/article/')) {
    return '/article'
  } else if (route.path.includes('/video/')) {
    return '/video'
  }

  return route.path
}

function getNtpUrl({ fullUrl = false }: { fullUrl?: boolean } = {}): string {
  if (fullUrl) {
    return window.location.href
  }
  return (
    'https://' +
    window.location.hostname +
    window.location.pathname +
    window.location.search
  )
}

function getNewsContentToggleState() {
  const newsToggleStore = useNewsToggleStore()
  const { newsToggle } = storeToRefs(newsToggleStore)
  return newsToggle.value ? 'on' : 'off'
}

function getCurrentThemeSetting() {
  const themeStore = useTheme()
  return themeStore.mixpanelPropertyName.value
}

export function getNtpAppVersion(): string {
  return version?.version || 'unknown'
}

/**
 * In order to allow MixPanel to sort by version, it'll help to send up a number (instead of string)
 * of our semver version number. '2.91.4' becomes 2091004.
 */
export function getNtpAppVersionAsNumber(): number | undefined {
  const semverString = getNtpAppVersion()

  const parts = semverString
    .split('.')
    .map((semverComponent) => parseInt(semverComponent, 10))

  if (parts.length !== 3 || parts.some(isNaN)) {
    return undefined
  }

  const [major, minor, patch] = parts
  return major * 1000000 + minor * 1000 + patch
}

export function getPageType(): NtpPageType {
  const path = window.location.pathname

  if (path.includes('article')) {
    return 'article'
  }
  if (path.includes('summary')) {
    return 'summary'
  }
  if (path.includes('video')) {
    return 'video'
  }
  return 'homepage'
}

export const waitUntilNotLoading = async (isLoading: Ref<boolean>) => {
  if (!isLoading.value) return

  await new Promise((resolve) => {
    const stop = watch(isLoading, (val) => {
      if (!val) {
        stop()
        resolve(true)
      }
    })
  })
}

/**
 * Properties that will be propagated with every event.
 */
export const getAugmentedProperties = async (
  siteSettings: Ref<SiteSettings> | undefined,
  eventName: string
): Promise<AugmentedProperties> => {
  const { articleSummary } = useUserSettings(siteSettings)
  const route = useRoute()

  const layoutSkeletonStore = useLayoutSkeletonStore()
  const { data: layoutSkeleton, isLoading } = storeToRefs(layoutSkeletonStore)

  const { ayData } = await useAssertiveYield()

  await waitUntilNotLoading(isLoading)

  const typeTag = await getTypeTag()

  const augmentedProperties: AugmentedProperties = {
    ntp_type_tag: typeTag,
    ntp_window_active:
      window.isPageVisible || document.visibilityState === 'visible',
    ntp_url: getNtpUrl(),
    ntp_app_version: getNtpAppVersion(),
    ntp_app_version_number: getNtpAppVersionAsNumber(),
    ntp_layout: getNtpPath(),
    ntp_page_type: getPageType(),
    ntp_assertive_data: ayData ?? undefined,
    ntp_news_content_toggle: getNewsContentToggleState(),
    ntp_is_user_idle: window.isUserIdle ?? false,
    ntp_user_settings: {
      theme: getCurrentThemeSetting(),
      ai_news_summary: { ...articleSummary },
    },
    pageview_id: getPageviewId(),
    user_id: getUserId(),
  }

  const seed = getPageviewSeed()
  if (seed !== null) {
    augmentedProperties.randomization_seed = seed
  }

  // NtpPage_Viewed, NtpAd_Rendered, and NtpMoreNews_Loaded events have extra properties related to screen size
  if (
    eventName === 'NtpPage_Viewed' ||
    eventName === 'NtpAd_Rendered' ||
    eventName === 'NtpMoreNews_Loaded'
  ) {
    const {
      browser_window_size,
      browser_window_height,
      browser_window_width,
      column_count,
    } = useScreenSizeProperties()

    augmentedProperties.browser_window_size = browser_window_size
    augmentedProperties.browser_window_height = browser_window_height
    augmentedProperties.browser_window_width = browser_window_width

    // Only add grid size on home page
    if (isHomepageRoute(route.path)) {
      augmentedProperties.column_count = column_count
    }
  }

  // Only add layout skeleton on home page
  if (isHomepageRoute(route.path)) {
    augmentedProperties.ntp_layout_skeleton = toRaw(layoutSkeleton.value?.name)
  }

  // Add fetched articles content for specific events.
  // Only attempt on homepage routes where column_count is available.
  //
  // NOTE: This populates the `visibleContent` property with ALL articles from the API response,
  // not just those visible in the viewport. The property name is kept for backwards compatibility
  // with existing analytics pipelines, but the content now represents all fetched articles.
  const isHomepage = isHomepageRoute(route.path)
  if (
    isHomepage &&
    (eventName === 'NtpPage_Viewed' || eventName === 'NtpMoreNews_Loaded')
  ) {
    try {
      // Get all articles from the appropriate batch based on event type
      // - NtpPage_Viewed: all articles from the initial page load
      // - NtpMoreNews_Loaded: all articles from the most recent "more news" batch
      let loadedContent
      if (eventName === 'NtpPage_Viewed') {
        if (!hasInitialBatch()) {
          logger.warn(
            `[visibleContent] No initial batch found for ${eventName}; sending event without visibleContent`
          )
          return augmentedProperties
        }
        loadedContent = getInitialBatchContent()
      } else {
        // NtpMoreNews_Loaded
        if (!hasMoreNewsBatch()) {
          logger.warn(
            `[visibleContent] No more-news batch found for ${eventName}; sending event without visibleContent`
          )
          return augmentedProperties
        }
        loadedContent = getLatestMoreNewsBatchContent()
      }

      if (loadedContent.length === 0) {
        // No content loaded yet - fail soft: log and continue without visibleContent
        logger.warn(
          `[visibleContent] No content found in batch for ${eventName}; sending event without visibleContent`
        )
        return augmentedProperties
      }

      // JIT map to add position_row and position_col using column_count
      const columnCount = augmentedProperties.column_count

      if (!columnCount || columnCount <= 0) {
        // column_count missing/invalid - fail soft: log and continue without visibleContent
        logger.warn(
          `[visibleContent] column_count missing/invalid (${columnCount}) for ${eventName}; sending event without visibleContent`
        )
        return augmentedProperties
      }

      augmentedProperties.visibleContent = loadedContent

      // Clear the more-news batch after capturing to prevent the same batch being sent again
      if (eventName === 'NtpMoreNews_Loaded') {
        clearMoreNewsBatch()
      }

      // Add test section content for NtpPage_Viewed only (test sections are not refreshed on more news)
      if (eventName === 'NtpPage_Viewed' && hasTestSectionContent()) {
        augmentedProperties.testSectionContent =
          getTestSectionContent() ?? undefined
        logger.log(
          `[testSectionContent] Added test section content to ${eventName}`
        )
      }
    } catch (error) {
      // If gathering visible content fails, log loudly but let the event proceed without visibleContent
      logger.warn('Failed to attach visible content', error)

      if (import.meta.dev || import.meta.test) {
        console.error(
          '[VISIBLE CONTENT ERROR]',
          `Tracking failed for ${eventName}:`,
          error
        )
      }
      // In all environments, continue without visibleContent
    }
  }

  return augmentedProperties
}

/**
 * The auto-layout function can re-rendered the page during re-size which can cause multiple `NtpAd_Rendered` events to fire for direct ads.
 * This function will send a mixpanel `NtpAd_Rendered` to be sent only once per page-view per ad position on the screen.
 */
const sentAdRenderedPlacements: string[] = []

export function shouldSendEvent(name: string, data: unknown): boolean {
  const isNtpAdEvent = (name: string, data: unknown): data is NtpAdEvent =>
    Boolean(name === 'NtpAd_Rendered' && data && typeof data === 'object')

  if (isNtpAdEvent(name, data)) {
    if (data.ntp_ad_provider !== 'direct' || !data.ntp_placement_name)
      return true
    if (sentAdRenderedPlacements.includes(data.ntp_placement_name)) {
      // If the event was sent for this placement; do not resend
      return false
    }
    // Record that an event is sent of this placement
    sentAdRenderedPlacements.push(data.ntp_placement_name)
  }
  return true
}

/* This helper determines if the provided event name corresponds to
 * an NTP news event, ensuring that the correct type is used for event handling.
 */
export function isNtpNewsEventContentType(
  data: Record<string, unknown>
): data is NtpNewsEvent {
  return (
    data.ntp_content_type === 'article' || data.ntp_content_type === 'video'
  )
}
